import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { webAuth } from '../api/client';

const passwordRules = [
  { test: (p) => p.length >= 8,            label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p),          label: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p),          label: 'One lowercase letter' },
  { test: (p) => /[0-9]/.test(p),          label: 'One number' },
  { test: (p) => /[^A-Za-z0-9]/.test(p),  label: 'One special character' },
];

export default function ResetPasswordScreen() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    const failedRules = passwordRules.filter(r => !r.test(password));
    if (failedRules.length > 0) { setError('Password does not meet all requirements.'); return; }

    setLoading(true);
    try {
      await webAuth.resetPassword(token, password, confirmPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={s.container}>
        <div style={s.card}>
          <div style={s.icon}>❌</div>
          <h2 style={s.title}>Invalid Link</h2>
          <p style={s.text}>This reset link is invalid.</p>
          <Link to="/forgot-password" style={s.button}>Request New Link</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={s.container}>
        <div style={s.card}>
          <div style={s.icon}>✅</div>
          <h2 style={s.title}>Password Reset</h2>
          <p style={s.text}>Your password has been reset. You can now sign in.</p>
          <button style={s.button} onClick={() => navigate('/login')}>Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={s.card}>
        <h1 style={s.title}>Reset Password</h1>
        <p style={s.subtitle}>Enter your new password below.</p>

        {error && <div style={s.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={s.field}>
            <label style={s.label}>New Password</label>
            <input style={s.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" required />
            <div style={s.rules}>
              {passwordRules.map(r => (
                <div key={r.label} style={{ ...s.rule, color: r.test(password) ? '#34C759' : '#999' }}>
                  {r.test(password) ? '✓' : '○'} {r.label}
                </div>
              ))}
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Confirm New Password</label>
            <input style={s.input} type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" required />
          </div>

          <button style={loading ? s.buttonDisabled : s.button} type="submit" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

const s = {
  container:    { minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  card:         { backgroundColor: '#fff', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px', textAlign: 'center' },
  title:        { fontSize: '28px', fontWeight: 'bold', color: '#1a1a2e', margin: '0 0 8px 0' },
  subtitle:     { color: '#666', fontSize: '14px', marginBottom: '28px' },
  icon:         { fontSize: 48, marginBottom: 16 },
  text:         { color: '#555', fontSize: 15, lineHeight: 1.6, marginBottom: 24 },
  field:        { marginBottom: '18px', textAlign: 'left' },
  label:        { display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#333' },
  input:        { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px', boxSizing: 'border-box', outline: 'none' },
  rules:        { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 },
  rule:         { fontSize: 13, transition: 'color 0.2s' },
  error:        { backgroundColor: '#ffe0e0', color: '#cc0000', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', textAlign: 'left' },
  button:       { display: 'block', width: '100%', padding: '14px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none', textAlign: 'center', boxSizing: 'border-box' },
  buttonDisabled: { width: '100%', padding: '14px', backgroundColor: '#999', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'not-allowed' },
};